import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Search, Copy, CheckCircle, Plus, Minus } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { apiClient } from '@/services/api/apiClient';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

interface MainMenuItem {
  menu_item_id: number;
  item_name: string;
  item_description: string;
  primary_price: number;
  section: string;
  dietary_flags?: any;
  is_beverage?: boolean;
}

interface MenuSection {
  section_name: string;
  items: MainMenuItem[];
}

interface SelectedItem extends MainMenuItem {
  price_adjustment: number;
  adjusted_price: number;
}

const SeasonalMenuCopyFromMain = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const [mainMenuItems, setMainMenuItems] = useState<MenuSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState<Map<number, SelectedItem>>(new Map());
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [copying, setCopying] = useState(false);
  const [priceAdjustment, setPriceAdjustment] = useState(0); // Percentage adjustment

  useEffect(() => {
    fetchMainMenuItems();
  }, []);

  const fetchMainMenuItems = async () => {
    try {
      setLoading(true);

      // Fetch main menu items using apiClient (works for both OAuth and demo users)
      const response = await apiClient.get('/menus/menu-items/regular');
      const data = response.data;
      
      if (!data.items || data.items.length === 0) {
        toast({
          title: 'No main menu found',
          description: 'Please upload a main menu first',
          variant: 'destructive'
        });
        navigate(`/seasonal-menu/${id}/items`);
        return;
      }
      
      // Group items by section
      const itemsBySection: { [key: string]: MainMenuItem[] } = {};
      data.items.forEach((item: any) => {
        const section = item.section || 'Uncategorized';
        if (!itemsBySection[section]) {
          itemsBySection[section] = [];
        }
        itemsBySection[section].push({
          menu_item_id: item.menu_item_id,
          item_name: item.item_name,
          item_description: item.item_description,
          primary_price: item.primary_price,
          section: section,
          dietary_flags: item.dietary_flags,
          is_beverage: item.is_beverage
        });
      });
      
      // Convert to sections array
      const sections = Object.entries(itemsBySection).map(([section_name, items]) => ({
        section_name,
        items
      }));
      
      setMainMenuItems(sections);
    } catch (error) {
      console.error('Error fetching main menu items:', error);
      toast({
        title: 'Error',
        description: 'Failed to load main menu items',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleItemToggle = (item: MainMenuItem, checked: boolean) => {
    const newSelected = new Map(selectedItems);
    
    if (checked) {
      const adjustedPrice = item.primary_price * (1 + priceAdjustment / 100);
      newSelected.set(item.menu_item_id, {
        ...item,
        price_adjustment: priceAdjustment,
        adjusted_price: Math.round(adjustedPrice * 100) / 100
      });
    } else {
      newSelected.delete(item.menu_item_id);
    }
    
    setSelectedItems(newSelected);
  };

  const handlePriceAdjustmentChange = (adjustment: number) => {
    setPriceAdjustment(adjustment);
    
    // Update all selected items with new adjustment
    const newSelected = new Map();
    selectedItems.forEach((item, id) => {
      const adjustedPrice = item.primary_price * (1 + adjustment / 100);
      newSelected.set(id, {
        ...item,
        price_adjustment: adjustment,
        adjusted_price: Math.round(adjustedPrice * 100) / 100
      });
    });
    
    setSelectedItems(newSelected);
  };

  const handleCopyItems = async () => {
    if (selectedItems.size === 0) {
      toast({
        title: 'No items selected',
        description: 'Please select at least one item to copy',
        variant: 'destructive'
      });
      return;
    }

    // Warn if price adjustment is being used
    if (priceAdjustment !== 0) {
      toast({
        title: 'Note',
        description: 'Price adjustment is not currently supported. Items will be copied with original prices. You can edit prices after copying.',
        variant: 'default'
      });
    }

    setCopying(true);
    try {
      // Get array of menu_item_ids from selected items
      const menuItemIds = Array.from(selectedItems.keys());

      // Use the copy-from-main endpoint that preserves ALL enriched data
      // (course_type, cuisine_type, allergen_info, menu_content, etc.)
      const response = await apiClient.post(`/seasonal-menus/${id}/copy-from-main`, {
        menu_item_ids: menuItemIds
      });

      const { copied_items, skipped_count } = response.data;
      const successCount = copied_items.length;
      const duplicateCount = skipped_count;

      if (successCount > 0 || duplicateCount > 0) {
        let description = '';
        if (successCount > 0) {
          description += `${successCount} item${successCount > 1 ? 's' : ''} copied successfully with all enriched data`;
        }
        if (duplicateCount > 0) {
          if (description) description += '. ';
          description += `${duplicateCount} item${duplicateCount > 1 ? 's' : ''} already existed`;
        }

        toast({
          title: successCount > 0 ? 'Success!' : 'Items Already Exist',
          description: description,
          variant: duplicateCount > 0 && successCount === 0 ? 'destructive' : 'default'
        });

        // Navigate back if at least some items were successful
        if (successCount > 0) {
          navigate(`/seasonal-menu/${id}/items`);
        }
      } else {
        throw new Error('Failed to copy any items');
      }
    } catch (error: any) {
      console.error('Error copying items:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to copy items';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setCopying(false);
      setShowReviewModal(false);
    }
  };

  const filteredSections = mainMenuItems.map(section => ({
    ...section,
    items: section.items.filter(item => 
      searchQuery === '' || 
      item.item_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.item_description?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(section => section.items.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5]">
      {/* Header */}
      <div className="border-b border-white/10 backdrop-blur-sm bg-white/5">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(`/seasonal-menu/${id}/items`)}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Seasonal Menu
            </button>
            <h1 className="text-xl font-semibold text-white">
              Copy from Main Menu
            </h1>
            <div className="w-32" /> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      {/* Search and Price Adjustment */}
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40 h-5 w-5" />
            <Input
              type="text"
              placeholder="Search menu items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
            />
          </div>
          
          {/* Price Adjustment Controls */}
          <div className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-medium">Price Adjustment</h3>
                <p className="text-white/60 text-sm">Apply to all selected items</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => handlePriceAdjustmentChange(priceAdjustment - 5)}
                  variant="outline"
                  size="sm"
                  className="bg-white text-[#1f4ed1] hover:bg-white/90 border-0"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="text-white font-semibold min-w-[80px] text-center">
                  {priceAdjustment > 0 ? '+' : ''}{priceAdjustment}%
                </span>
                <Button
                  onClick={() => handlePriceAdjustmentChange(priceAdjustment + 5)}
                  variant="outline"
                  size="sm"
                  className="bg-white text-[#1f4ed1] hover:bg-white/90 border-0"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 pb-32 max-w-6xl">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
            <p className="text-white/60 mt-4">Loading main menu items...</p>
          </div>
        ) : filteredSections.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-white/60">No items found</p>
          </div>
        ) : (
          <div className="space-y-8">
            {filteredSections.map((section, sectionIndex) => (
              <motion.div
                key={section.section_name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: sectionIndex * 0.1 }}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {section.section_name}
                </h2>
                <div className="grid gap-4">
                  {section.items.map((item) => {
                    const isSelected = selectedItems.has(item.menu_item_id);
                    const selectedItem = selectedItems.get(item.menu_item_id);
                    
                    return (
                      <motion.div
                        key={item.menu_item_id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className={`bg-white/10 backdrop-blur-md rounded-xl border p-6 transition-all ${
                          isSelected ? 'border-yellow-400/50 bg-yellow-400/5' : 'border-white/20'
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => handleItemToggle(item, checked as boolean)}
                            className="mt-1 border-white/40 data-[state=checked]:bg-yellow-400 data-[state=checked]:border-yellow-400"
                          />
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-white mb-1">
                              {item.item_name}
                            </h3>
                            {item.item_description && (
                              <p className="text-white/70 text-sm mb-2">
                                {item.item_description}
                              </p>
                            )}
                            <div className="flex items-center gap-4">
                              <span className="text-white/60">
                                Original: ${item.primary_price.toFixed(2)}
                              </span>
                              {isSelected && selectedItem && (
                                <span className="text-yellow-400 font-semibold">
                                  Seasonal: ${selectedItem.adjusted_price.toFixed(2)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Sticky Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-black/80 backdrop-blur-md border-t border-white/20 p-4">
        <div className="container mx-auto max-w-6xl flex justify-between items-center">
          <p className="text-white/60 text-sm">
            {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
          </p>
          <Button
            onClick={() => selectedItems.size > 0 && setShowReviewModal(true)}
            disabled={selectedItems.size === 0}
            className="bg-yellow-400 text-black hover:bg-yellow-300 font-bold px-8 py-3 rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center gap-2"
          >
            <Copy className="h-5 w-5" />
            Review & Copy Items
          </Button>
        </div>
      </div>

      {/* Review Modal */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] border-0 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl font-bold">Review Selected Items</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-white/80">
              You are about to copy {selectedItems.size} items to your seasonal menu.
            </p>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {Array.from(selectedItems.values()).map((item) => (
                <div key={item.menu_item_id} className="border-b border-white/20 pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-white">{item.item_name}</h4>
                      <p className="text-sm text-white/60">{item.section}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-white/50 line-through">
                        ${item.primary_price.toFixed(2)}
                      </p>
                      <p className="font-semibold text-[#4ade80]">
                        ${item.adjusted_price.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="flex gap-4">
            <Button
              variant="outline"
              onClick={() => setShowReviewModal(false)}
              disabled={copying}
              className="border-2 border-white text-white bg-transparent hover:bg-white hover:text-[#1f4ed1] transition-all"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCopyItems}
              disabled={copying}
              className="bg-[#FDB913] text-black hover:bg-[#FDB913]/90 font-bold shadow-lg"
            >
              {copying ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2" />
                  Copying...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Copy to Seasonal Menu
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SeasonalMenuCopyFromMain;